import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import SearchField from './search-field';
import LoadingAnimation from '../loading-animation/';
import './search-selector.scss';
import debounce from 'lodash.debounce';

const SearchSelector = ({assistiveText, disabled, disableSearch, fieldIdentifier, hideList, includeSortOption, isSelected, items, isLoading, label, loadingText, onAddClick, onBlur, onBlurWithNewItem, onChange, onDeselect, onSearch, onSelect, searchingText, value, zIndex}) => {
	const [isListVisible, setIsListVisible] = useState(false);
	const [isSearching, setIsSearching] = useState(false);
	const [inputValue, setInputValue] = useState();
	const [sortByLastName, setSortByLastName] =  useState(false);
	const [localItems, setLocalItems] = useState([]);
	const [forceAdd, setForceAdd] = useState(false);
	const fieldId = `search${fieldIdentifier}Input`;
	const debounceOnSearch = useCallback(debounce(handleSearch, 500), []);
	const ignoreBlurRef = React.useRef(false);
	const resultsListRef = useRef(null);
	const searchFieldRef = useRef();

	const handleClickOutside = (event) => {
		if (event.target.className !== 'search-input' && resultsListRef.current && !resultsListRef.current.contains(event.target)) {
			setIsListVisible(false);
		}
	}

	useEffect(() => {
		setInputValue(value && value.name);
	}, [value]);

	useEffect(() => {
		setIsSearching(false);
	}, [items]);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
	    document.removeEventListener('mousedown', handleClickOutside);
	  }
	}, []);

	const renderItem = (item) => {
		return <li key={item.id} onMouseDown={
			(e) => {
				handleSelection(item, e)}
			} className={inputValue === item ? 'selected': null} >
      {item.name}
    </li>
  }

	const handleFocus = () => {
		if (!hideList) {
			setIsListVisible(true);
		} else {
			setIsListVisible(false);
		}
	}

	const handleBlur = () => {
		if (ignoreBlurRef.current === true) {
			return;
		}
		setIsListVisible(false);
		const matchedItem = items && items.find(item => item.name === inputValue);
		if (matchedItem) {
			handleSelection(matchedItem);
		} else {
			onBlurWithNewItem && onBlurWithNewItem(inputValue);
		}
	}

	function handleChange (val) {
		setInputValue(val);
		onChange && onChange(val);
		debounceOnSearch(val, items);
	}

	// TODO: Fix type Kho, then backspace > results don't update
  function handleSearch (inputValue, items) {
		if (disableSearch) {
			return;
		}
		setForceAdd(false);
		let possibleItems = [];
		if (inputValue && inputValue !== '' && items) {
			possibleItems = items.filter(item => {
				return item.name && item.name.toLowerCase().includes(inputValue.toLowerCase());
			});
		}
		if (possibleItems.length) {
			setLocalItems(possibleItems);
		} else {
			setLocalItems([]);
			setIsSearching(true);
			onSearch && onSearch(inputValue);
		}
	}

	const handleSelection = (val) => {
		setIsListVisible(false);
		onSelect(val);
	}

	const handleSelectionCleared = () => {
		onDeselect && onDeselect(inputValue);
	}

	function addItem () {
		onAddClick({value: inputValue.trim(), sortByLastName: sortByLastName});
		setIsListVisible(false);
		setSortByLastName(false);
	}

	function getDropdownClass () {
		const searchField = searchFieldRef.current;
		const distanceFromBottom = window.innerHeight - searchField.getBoundingClientRect().top + searchField.offsetHeight;

		if (distanceFromBottom < 400) {
			return 'selector-menu drop-up'
		}
		return 'selector-menu';
	}

	function getSearchSelectorClass() {
		let baseClass = 'form-element selector';
		if (disabled) {
			baseClass += ' disabled';
		}
		if (disableSearch) {
			baseClass += ' search-disabled';
		}
		return baseClass;
	}

	const handleSortByChange = () => {
		setSortByLastName(!sortByLastName);
	}

	const renderList = () => {
		const noResultsNewText = `${inputValue} does not exist, would you like to add it?`;
		const noResultsText = assistiveText.noResults ? assistiveText.noResults : `No results found for ${inputValue}`;
		const createNewText = assistiveText.createNew ? assistiveText.createNew : `Create New ${fieldIdentifier}`;
		const itemsList = localItems.length ? localItems : items;
		const listItems = itemsList.map(
      (item) => renderItem(item)
    );

		if (isLoading) {
			return (
				<LoadingAnimation text={loadingText} />
			);
		} else if (isSearching && !isLoading) {
			return (
				<LoadingAnimation text={searchingText} />
			);
		} else if (listItems.length && !forceAdd) {
			return (
				<ul>
					{listItems}
					{onAddClick && <li className="add-new"><button className="primary" disabled={!(inputValue && inputValue.length)} onClick={(evt) => {setForceAdd(true);}}>Add {fieldIdentifier}</button></li>}
				</ul>
			);
		} else if (onAddClick && inputValue && inputValue.length) {
			return (
				<div className="no-results">
					<h2>{inputValue.length ? noResultsNewText : noResultsText}</h2>
					<div className="create-options">
						<h3>{createNewText}</h3>
						<h4>{inputValue}</h4>
						{ includeSortOption &&
							<div className="create-input"><input className="search-input" type="checkbox" id="sortByLastName" onChange={handleSortByChange} /><label htmlFor="sortByLastName">Sort By Last Name</label></div>
						}
		        		<button className="primary" onMouseDown={() => {addItem()}}>Add {fieldIdentifier}</button>
					</div>
				</div>
			);
		}
	}
	return (
		<div ref={searchFieldRef} className={getSearchSelectorClass()} style={{zIndex: (zIndex || 2)}} >
			<h2><label htmlFor={ fieldId }>{label ? label: `${fieldIdentifier} Name:`}</label></h2>
			<SearchField
				onChange={ handleChange }
				onSelectionCleared={ handleSelectionCleared }
				onBlur={ handleBlur }
				onFocus={ handleFocus }
				placeholder={disableSearch ? 'Selection required' : ''}
				value={ typeof inputValue === 'string' ? inputValue : (value && value.name)}
				isSelected={isSelected}
				inputId={ fieldId }
				disabled={ disabled }
			/>
			{isListVisible &&
				<div className={getDropdownClass()} onMouseDown={() => {ignoreBlurRef.current = true;}}><div className="results-list" ref={resultsListRef}>{renderList()}</div></div>
			}
		</div>
	)
};

SearchSelector.propTypes = {
	items: PropTypes.arrayOf(PropTypes.object),
	label: PropTypes.string,
	onBlurWithNewItem: PropTypes.func
}

export default SearchSelector
